'use client';

import { c as _c } from "react/compiler-runtime";
import dynamic from 'next/dynamic';
import { Button } from '@/app/components/button';
import { ButtonGroup } from '@/app/components/button-group';
import { IconPlayArrow } from '@/app/components/icons/audio-video/icon-play-arrow';
import { Container } from '@/app/components/layouts/container';
import { Image } from '@/app/components/media/image';
import { generateSources } from '@/app/components/media/image/generate-sources';
import { useOverlayMountControl } from '@/app/components/overlays/hooks/useOverlayMountControl';
import { OVERLAY_KEYS } from '@/app/constants/overlayKeys';
import { CV_PRODUCT_EVENT_GROUP, CV_PRODUCT_EVENT_NAME } from '@/app/cv/components/sections/products/tracking';
import type { ProductBannerItemProps } from '@/app/cv/components/sections/products/types';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
import { scrollIntoViewIfNeeded } from '@/app/utils/scrollIntoViewIfNeed';
const IFrameVideoModal = dynamic(() => import('@/app/components/overlays/modal/video-modal').then(mod => mod.IFrameVideoModal), {
  ssr: false
});
const parseSectionHref = (href?: string): string | undefined => {
  return href?.match(/^\?section=(.+)/)?.[1];
};
const scrollToSection = (sectionId: string) => {
  const element = document.getElementById(sectionId);
  if (!element) return;
  scrollIntoViewIfNeeded(element);
};
interface SliderContentProps {
  index: number;
  item: ProductBannerItemProps;
  multiSlide?: boolean;
  isActive?: boolean;
}
export const SliderContent = t0 => {
  const $ = _c(49);
  const {
    index: t1,
    item,
    multiSlide: t2,
    isActive: t3
  } = t0;
  const index = t1 === undefined ? 0 : t1;
  const multiSlide = t2 === undefined ? false : t2;
  const isActive = t3 === undefined ? true : t3;
  const {
    title,
    description,
    logo,
    logoDesktop,
    image,
    imageDesktop,
    youtubeId,
    buttons: t4
  } = item;
  let t5;
  if ($[0] !== t4) {
    t5 = t4 === undefined ? [] : t4;
    $[0] = t4;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const buttons = t5;
  const instanceId = `hero-banner-youtube-${index}`;
  let t6;
  if ($[2] !== instanceId) {
    t6 = {
      overlayKey: OVERLAY_KEYS.MODAL_VIDEO,
      instanceId
    };
    $[2] = instanceId;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const {
    shouldRender,
    open: openYoutubeModal
  } = useOverlayMountControl(t6);
  let t7;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = cn("relative h-full min-w-full", "before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:z-1 before:h-50 before:bg-linear-to-b before:from-black/25 before:to-transparent", "xl:before:h-full xl:before:bg-linear-to-tr xl:before:from-black/45 xl:before:to-50%", "after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:z-1 after:h-75 after:bg-linear-to-t after:from-black/25 after:to-transparent", "xl:after:content-none");
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const t8 = `slider-${index}`;
  const t9 = isActive ? true : undefined;
  let t10;
  if ($[5] !== t8 || $[6] !== t9) {
    t10 = createDataAttributes({
      test: t8,
      active: t9
    });
    $[5] = t8;
    $[6] = t9;
    $[7] = t10;
  } else {
    t10 = $[7];
  }
  let t11;
  if ($[8] !== image || $[9] !== imageDesktop || $[10] !== index || $[11] !== title) {
    t11 = !!image && <Image priority={index === 0} rootMargin="0px 0px 0px -2px" src={image} alt={title ?? "ISUZU Truck"} width={600} height={800} layout="fill" objectFit="cover" sources={generateSources("overall", {
      mobileSrc: image,
      mobileOptions: {
        crop: "3:4"
      },
      desktopSrc: imageDesktop ?? image,
      desktopOptions: {
        crop: "16:9"
      }
    })} />;
    $[8] = image;
    $[9] = imageDesktop;
    $[10] = index;
    $[11] = title;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== multiSlide) {
    t12 = cn("absolute inset-0 z-10 flex h-full flex-col justify-between pt-6 pb-15 md:pt-10 md:pb-19 xl:justify-end xl:gap-y-6 xl:pt-14 xl:pb-16", {
      "xl:pb-24": multiSlide
    });
    $[13] = multiSlide;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  const t13 = !title;
  let t14;
  if ($[15] !== t13) {
    t14 = cn("flex h-full flex-col xl:justify-end", {
      "gap-y-4": t13
    });
    $[15] = t13;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  let t15;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = createDataAttributes({
      test: "slider-heading"
    });
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  let t16;
  if ($[18] !== index || $[19] !== logo || $[20] !== logoDesktop || $[21] !== title) {
    t16 = title ? <h1 className="font-display text-h2 text-center text-white uppercase xl:text-left" {...createDataAttributes({
      test: "slider-title"
    })}>{title}</h1> : !!(logo ?? logoDesktop) && <Image className="mx-auto w-full max-w-50 md:max-w-75 xl:mr-auto xl:ml-0" imageClassName="xl:object-bottom-left" priority={index === 0} rootMargin="0px 50% 0px 0px" src={logo ?? logoDesktop ?? ""} alt={title ?? "logo"} width={300} height={72} sizes="(min-width: 1280px) 320px, 256px" objectFit="contain" objectPosition="bottom" aspectRatio="calculate" sources={[{
      srcset: logoDesktop ?? logo ?? "",
      media: {
        minWidth: 1280
      },
      options: {
        width: 320
      }
    }, {
      srcset: logo ?? logoDesktop ?? "",
      media: {
        maxWidth: 1279
      },
      options: {
        width: 256
      }
    }]} />;
    $[18] = index;
    $[19] = logo;
    $[20] = logoDesktop;
    $[21] = title;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== description || $[24] !== title) {
    t17 = !!description && <h2 className={cn("text-sub-h2 font-noto-bold where-span:text-inline-wrap mx-auto max-w-72 text-center text-white md:max-w-96 xl:mx-0 xl:text-left", {
      "mb-4": title
    })} dangerouslySetInnerHTML={{
      __html: description
    }} {...createDataAttributes({
      test: "slider-description"
    })} />;
    $[23] = description;
    $[24] = title;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] !== buttons || $[27] !== openYoutubeModal || $[28] !== youtubeId) {
    t18 = (!!youtubeId || buttons.length > 0) && <ButtonGroup className="mt-auto justify-center xl:mt-0 xl:justify-start" {...createDataAttributes({
      test: "slider-button-group"
    })}>{buttons.map(_temp)}{!!youtubeId && <Button className="w-full md:w-auto xl:ml-auto" color="white" variant="tertiary" size="sm" iconBefore={<IconPlayArrow />} onClick={openYoutubeModal} {...createDataAttributes({
        test: "youtube-button"
      })}>ดูวิดีโอ</Button>}</ButtonGroup>;
    $[26] = buttons;
    $[27] = openYoutubeModal;
    $[28] = youtubeId;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== t14 || $[31] !== t16 || $[32] !== t17 || $[33] !== t18) {
    t19 = <div className={t14} {...t15}>{t16}{t17}{t18}</div>;
    $[30] = t14;
    $[31] = t16;
    $[32] = t17;
    $[33] = t18;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== t12 || $[36] !== t19) {
    t20 = <Container className={t12}>{t19}</Container>;
    $[35] = t12;
    $[36] = t19;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== t10 || $[39] !== t11 || $[40] !== t20) {
    t21 = <div className={t7} {...t10}>{t11}{t20}</div>;
    $[38] = t10;
    $[39] = t11;
    $[40] = t20;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== instanceId || $[43] !== shouldRender || $[44] !== youtubeId) {
    t22 = !!shouldRender && !!youtubeId && <IFrameVideoModal instanceId={instanceId} youtubeId={youtubeId} />;
    $[42] = instanceId;
    $[43] = shouldRender;
    $[44] = youtubeId;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== t21 || $[47] !== t22) {
    t23 = <>{t21}{t22}</>;
    $[46] = t21;
    $[47] = t22;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  return t23;
};
function _temp(t0, index_0) {
  const {
    href,
    ...button
  } = t0;
  const sectionId = parseSectionHref(href);
  return <Button key={index_0} color="white" className="w-full md:w-auto" {...sectionId ? button : {
    href,
    ...button
  }} {...sectionId && {
    onClick: () => scrollToSection(sectionId)
  }} {...createDataAttributes({
    test: `button-${index_0}`
  })} {...createDataGA({
    eventName: CV_PRODUCT_EVENT_NAME,
    group: CV_PRODUCT_EVENT_GROUP.HERO_BANNER,
    click: true,
    text: button.children,
    url: href
  })} />;
}